/**
 * The visual mapper's data model: per-resource target FHIR fields (derived from
 * the @ai/fhir profiled subset + the infer heuristic), the transform library,
 * and the form↔spec round-trip + validation helpers. The mapper produces exactly
 * the FhirMappingSpec the backend's start-fhir-import expects, so the visual UI
 * is a pure presentation layer over the same JSON the JSON-editor produced.
 */

export interface FieldMap {
  from?: string;
  combine?: string[];
  separator?: string;
  const?: string | number | boolean;
  transform?: string;
}
export interface RefMap {
  resourceType: string;
  from: string;
}
export interface MappingSpec {
  sourceSystem: string;
  resourceType: string;
  identity: string;
  fields: Record<string, FieldMap>;
  references?: Record<string, RefMap>;
}

/** Named value transforms (mirrors packages/fhir/src/transforms.ts). '' = none. */
export const TRANSFORMS: ReadonlyArray<{ name: string; desc: string }> = [
  { name: '', desc: '— no transform —' },
  { name: 'trim', desc: 'Trim whitespace' },
  { name: 'lowercase', desc: 'Lowercase' },
  { name: 'uppercase', desc: 'Uppercase' },
  { name: 'sex_to_gender', desc: 'Sex word/letter → FHIR gender' },
  { name: 'phone_digits', desc: 'Strip to digits only' },
  { name: 'to_number', desc: 'Parse to a number' },
  { name: 'date_mdy_to_iso', desc: 'MM/DD/YYYY → YYYY-MM-DD' },
  { name: 'date_to_iso', desc: 'Any date → YYYY-MM-DD' },
];

export interface TargetField {
  path: string;
  label: string;
  required?: boolean;
  suggestTransform?: string;
}
export interface ResourceDef {
  resourceType: string;
  /** Patient-link reference field for non-Patient resources. */
  referenceKey?: 'subject' | 'patient';
  fields: TargetField[];
}

export const RESOURCE_DEFS: ReadonlyArray<ResourceDef> = [
  {
    resourceType: 'Patient',
    fields: [
      { path: 'name.0.family', label: 'Last name', required: true },
      { path: 'name.0.given.0', label: 'First name', required: true },
      { path: 'name.0.given.1', label: 'Middle name' },
      { path: 'birthDate', label: 'Date of birth', suggestTransform: 'date_to_iso' },
      { path: 'gender', label: 'Sex / gender', suggestTransform: 'sex_to_gender' },
      { path: 'address.0.line.0', label: 'Address line 1' },
      { path: 'address.0.city', label: 'City' },
      { path: 'address.0.state', label: 'State' },
      { path: 'address.0.postalCode', label: 'ZIP' },
    ],
  },
  {
    resourceType: 'Condition',
    referenceKey: 'subject',
    fields: [
      { path: 'code.text', label: 'Diagnosis / problem', required: true },
      { path: 'code.coding.0.code', label: 'ICD-10 code' },
      { path: 'clinicalStatus.coding.0.code', label: 'Status (active/resolved)' },
      { path: 'recordedDate', label: 'Recorded date', suggestTransform: 'date_to_iso' },
    ],
  },
  {
    resourceType: 'AllergyIntolerance',
    referenceKey: 'patient',
    fields: [
      { path: 'code.text', label: 'Substance / allergen', required: true },
      { path: 'reaction.0.manifestation.0.text', label: 'Reaction' },
      { path: 'reaction.0.severity', label: 'Severity' },
      { path: 'onsetDateTime', label: 'Onset date', suggestTransform: 'date_to_iso' },
    ],
  },
  {
    resourceType: 'MedicationStatement',
    referenceKey: 'subject',
    fields: [
      { path: 'medicationCodeableConcept.text', label: 'Drug name', required: true },
      { path: 'status', label: 'Status' },
      { path: 'dosage.0.text', label: 'Dosage / sig' },
    ],
  },
  {
    resourceType: 'Observation',
    referenceKey: 'subject',
    fields: [
      { path: 'code.text', label: 'Measurement / test', required: true },
      { path: 'valueQuantity.value', label: 'Value', suggestTransform: 'to_number' },
      { path: 'valueQuantity.unit', label: 'Unit' },
      { path: 'status', label: 'Status' },
      { path: 'effectiveDateTime', label: 'Date taken', suggestTransform: 'date_to_iso' },
    ],
  },
  {
    resourceType: 'Immunization',
    referenceKey: 'patient',
    fields: [
      { path: 'vaccineCode.text', label: 'Vaccine', required: true },
      { path: 'vaccineCode.coding.0.code', label: 'CVX code' },
      { path: 'status', label: 'Status' },
      { path: 'occurrenceDateTime', label: 'Date given', suggestTransform: 'date_to_iso' },
    ],
  },
  {
    resourceType: 'Practitioner',
    fields: [
      { path: 'name.0.family', label: 'Last name', required: true },
      { path: 'name.0.given.0', label: 'First name' },
      { path: 'identifier.0.value', label: 'NPI' },
    ],
  },
  {
    resourceType: 'DocumentReference',
    referenceKey: 'subject',
    fields: [
      { path: 'description', label: 'Title', required: true },
      { path: 'type.text', label: 'Document type' },
      { path: 'content.0.attachment.url', label: 'File path / URL' },
      { path: 'date', label: 'Document date', suggestTransform: 'date_to_iso' },
    ],
  },
  {
    resourceType: 'Encounter',
    referenceKey: 'subject',
    fields: [
      { path: 'type.0.text', label: 'Appointment / visit type', required: true },
      { path: 'period.start', label: 'Start (date/time)', suggestTransform: 'date_to_iso' },
      { path: 'period.end', label: 'End (date/time)', suggestTransform: 'date_to_iso' },
      { path: 'status', label: 'Status' },
      { path: 'reasonCode.0.text', label: 'Reason / note' },
    ],
  },
];

export const RESOURCE_TYPES = RESOURCE_DEFS.map((d) => d.resourceType);
export const defFor = (resourceType: string): ResourceDef | undefined =>
  RESOURCE_DEFS.find((d) => d.resourceType === resourceType);

/** One target field's assignment in the visual board. */
export interface FieldAssignment {
  column?: string;
  const?: string;
  transform?: string;
}
export interface MapperForm {
  resourceType: string;
  sourceSystem: string;
  identity: string;
  assignments: Record<string, FieldAssignment>;
  /** Source column that links each row to its patient (referenceKey resources). */
  referenceColumn: string;
}

/** Build the FhirMappingSpec the backend expects from the visual-board form. */
export function formToSpec(form: MapperForm): MappingSpec {
  const fields: Record<string, FieldMap> = {};
  for (const [path, a] of Object.entries(form.assignments)) {
    if (a.const != null && a.const !== '') fields[path] = { const: a.const };
    else if (a.column) fields[path] = a.transform ? { from: a.column, transform: a.transform } : { from: a.column };
  }
  const spec: MappingSpec = {
    sourceSystem: form.sourceSystem || 'flat-file',
    resourceType: form.resourceType,
    identity: form.identity,
    fields,
  };
  const def = defFor(form.resourceType);
  if (def?.referenceKey && form.referenceColumn) {
    spec.references = { [def.referenceKey]: { resourceType: 'Patient', from: form.referenceColumn } };
  }
  return spec;
}

/** Hydrate the visual board from a spec (used by "Suggest" + JSON→board). */
export function specToForm(spec: MappingSpec): MapperForm {
  const assignments: Record<string, FieldAssignment> = {};
  for (const [path, fm] of Object.entries(spec.fields ?? {})) {
    if (fm.const != null) assignments[path] = { const: String(fm.const) };
    else if (fm.from) assignments[path] = { column: fm.from, transform: fm.transform ?? '' };
  }
  const def = defFor(spec.resourceType);
  let referenceColumn = '';
  if (def?.referenceKey) referenceColumn = spec.references?.[def.referenceKey]?.from ?? '';
  return {
    resourceType: spec.resourceType,
    sourceSystem: spec.sourceSystem,
    identity: spec.identity,
    assignments,
    referenceColumn,
  };
}

/** Operator-facing validation (the authoritative check is the server's Zod). */
export function validateSpec(spec: MappingSpec, headers: string[]): string[] {
  const errors: string[] = [];
  const hset = new Set(headers);
  if (!spec.identity) errors.push('Pick the identity column (each row’s own id).');
  else if (headers.length > 0 && !hset.has(spec.identity)) errors.push(`Identity column “${spec.identity}” is not in the file.`);

  const def = defFor(spec.resourceType);
  for (const f of def?.fields ?? []) {
    if (f.required && !spec.fields[f.path]) errors.push(`“${f.label}” is required.`);
  }
  for (const [path, fm] of Object.entries(spec.fields)) {
    if (fm.from && headers.length > 0 && !hset.has(fm.from)) errors.push(`Column “${fm.from}” (mapped to ${path}) is not in the file.`);
  }
  if (def?.referenceKey && !spec.references?.[def.referenceKey]) {
    errors.push('Pick the column that links each row to its patient (MRN).');
  }
  return errors;
}
